/**
 * 🚀 UNIVERSAL TEXT ELEMENT 2025
 * 
 * Dynamisches Text-Element mit TipTap für HeroHeader
 * - Voll responsive (Desktop/Mobile)
 * - Rich Text Support
 * - Individuelle Styling-Optionen pro Element
 */

import React, { useMemo, useState } from 'react';
import TipTapInlineEditor from './TipTapInlineEditor';
import AnimatedElement from './AnimatedElement';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import logger from '../../utils/logger';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalTextElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    fonts = {},
    availableFonts = [],
    layoutConfig = {},
    isCurrentlyEditing = false,
    onEditingStart = () => {},
    onEditingEnd = () => {}
}) {
    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    const [localStyles, setLocalStyles] = useState({
        fontSize: element.fontSize,
        fontSizeMobile: element.fontSizeMobile,
        fontWeight: element.fontWeight,
        fontWeightMobile: element.fontWeightMobile,
        marginTop: element.marginTop,
        marginRight: element.marginRight,
        marginBottom: element.marginBottom,
        marginLeft: element.marginLeft,
        marginTopMobile: element.marginTopMobile,
        marginRightMobile: element.marginRightMobile,
        marginBottomMobile: element.marginBottomMobile,
        marginLeftMobile: element.marginLeftMobile,
        maxWidth: element.maxWidth,
        maxWidthMobile: element.maxWidthMobile,
        lineHeight: element.lineHeight,
        lineHeightMobile: element.lineHeightMobile,
        letterSpacing: element.letterSpacing,
        letterSpacingMobile: element.letterSpacingMobile,
        // ✅ Text Box (Badge/Tag Style)
        textBoxEnabled: element.textBoxEnabled,
        textBoxPaddingHorizontal: element.textBoxPaddingHorizontal,
        textBoxPaddingVertical: element.textBoxPaddingVertical,
        textBoxBackgroundColor: element.textBoxBackgroundColor,
        textBoxBorderEnabled: element.textBoxBorderEnabled,
        textBoxBorderWidth: element.textBoxBorderWidth,
        textBoxBorderColor: element.textBoxBorderColor,
        textBoxBorderRadius: element.textBoxBorderRadius
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // ✅ Text Alignment für Flex-Positionierung
    const currentTextAlign = isMobileLayout ? 
        (element.textAlignMobile || element.textAlign || 'left') : 
        (element.textAlign || 'left');
    
    // ✅ Container Style - Handled Alignment via Flexbox
    const containerStyle = {
        // ✅ NICHT width: 100% - wird durch flex: 1 in LayoutGridElement.css gesteuert!
        display: 'flex',
        justifyContent: currentTextAlign === 'center' ? 'center' : 
                        currentTextAlign === 'right' ? 'flex-end' : 'flex-start',
        // ✅ Flex control: Text should grow to fill available space (in row layouts)
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto'
    };

    // ✅ WICHTIG: Margins NICHT an TipTap übergeben (werden über Props gesteuert)
    // ✅ WICHTIG: width/maxWidth NICHT an TipTap übergeben (werden über Props gesteuert)
    // Nur Text-Styling Properties!
    
    // ✅ FONT FALLBACK FIX: H1 verwendet globalBigFont, Rest verwendet globalSmallFont
    const getFontFallback = () => {
        if (element.fontFamily) return element.fontFamily;
        // H1 → Big Font, alles andere → Small Font
        return element.elementType === 'h1' 
            ? (fonts.globalBigFont || 'inherit')
            : (fonts.globalSmallFont || 'inherit');
    };
    
    // ⚡ OPTIMISTIC: Use local state OR props
    const activeFontSize = isMobileLayout 
        ? (localStyles.fontSizeMobile ?? (element.fontSizeMobile ?? (localStyles.fontSize ?? (element.fontSize ?? 16))))
        : (localStyles.fontSize ?? (element.fontSize ?? 16));
    const activeFontWeight = isMobileLayout
        ? (localStyles.fontWeightMobile ?? (element.fontWeightMobile ?? (localStyles.fontWeight ?? (element.fontWeight ?? '400'))))
        : (localStyles.fontWeight ?? (element.fontWeight ?? '400'));
    // ✅ DEPRECATED: lineHeight & letterSpacing werden jetzt via CSS Variables + Media Query gehandhabt
    // Keine JS-basierte Mobile-Detection mehr nötig für diese Properties

    const textStyle = {
        ...((!element.textEffect || element.textEffect === 'none') && {
            color: layoutConfig.overlayMode ? '#ffffff' : (element.color || '#333333')
        }),
        fontSize: appendPx(activeFontSize),
        fontWeight: activeFontWeight,
        fontFamily: getFontFallback(),
        textAlign: currentTextAlign,
        ...getTextEffectStyles(element.textEffect),
        // ✅ CSS Variables für Responsive System (wie fontSize/fontWeight)
        '--normal-line-height': element.lineHeight || 1.6,
        '--mobile-line-height': element.lineHeightMobile || element.lineHeight || 1.6,
        '--normal-letter-spacing': appendPx(element.letterSpacing ?? 0),
        '--mobile-letter-spacing': appendPx(element.letterSpacingMobile ?? element.letterSpacing ?? 0)
    };
    
    // 🔍 DEBUG: Log maxWidth vor TipTapInlineEditor rendering (nur Development)
    // const debugMaxWidth = localStyles.maxWidth ?? (parseInt(element.maxWidth) || 800);
    // const debugMaxWidthMobile = localStyles.maxWidthMobile ?? (parseInt(element.maxWidth) || 600);
    // logger.dev(`📏 [UniversalTextElement] Rendering text with maxWidth:`, {
    //     elementId: element.id,
    //     elementType: element.elementType,
    //     rawMaxWidth: element.maxWidth,
    //     rawMaxWidthMobile: element.maxWidthMobile,
    //     parsedMaxWidth: parseInt(element.maxWidth),
    //     finalMaxWidth: debugMaxWidth,
    //     finalMaxWidthMobile: debugMaxWidthMobile,
    //     isEditMode
    // });

    return (
        <AnimatedElement
            animationEnabled={element.animationEnabled}
            animationType={element.animationType}
            animationSpeed={element.animationSpeed}
            isEditMode={isEditMode}
            triggerAnimation={element.triggerAnimation}
            style={containerStyle}
        >
            {/* 🔧 TIPTAP INLINE EDITOR - Für alle Texte */}
            <TipTapInlineEditor
                    value={element.content || ''}
                    onChange={(newValue) => {
                        if (onUpdate) {
                            onUpdate({ ...element, content: newValue });
                        }
                    }}
                    isEditMode={isEditMode}
                    placeholder={element.placeholder || 'Enter text...'}
                    element="div"
                    enableRichText={true}
                    showFloatingToolbar={true}
                    colors={colors}
                    brandFonts={{
                        globalBigFont: fonts?.globalBigFont,
                        globalSmallFont: fonts?.globalSmallFont
                    }}
                    forceCloseToolbar={!isCurrentlyEditing}
                    onFocus={onEditingStart}
                    onBlur={onEditingEnd}
                    
                    // Margins (⚡ OPTIMISTIC)
                    currentMargins={{
                        top: localStyles.marginTop ?? element.marginTop ?? 0,
                        right: localStyles.marginRight ?? element.marginRight ?? 0,
                        bottom: localStyles.marginBottom ?? element.marginBottom ?? 0,
                        left: localStyles.marginLeft ?? element.marginLeft ?? 0
                    }}
                    onStyleChange={(margins) => {
                        setLocalStyles(prev => ({
                            ...prev,
                            marginTop: margins.top,
                            marginRight: margins.right,
                            marginBottom: margins.bottom,
                            marginLeft: margins.left
                        }));
                        debouncedParentUpdate({
                            ...element,
                            marginTop: margins.top,
                            marginRight: margins.right,
                            marginBottom: margins.bottom,
                            marginLeft: margins.left
                        });
                    }}
                    currentMarginsMobile={{
                        top: localStyles.marginTopMobile ?? element.marginTopMobile ?? 0,
                        right: localStyles.marginRightMobile ?? element.marginRightMobile ?? 0,
                        bottom: localStyles.marginBottomMobile ?? element.marginBottomMobile ?? 0,
                        left: localStyles.marginLeftMobile ?? element.marginLeftMobile ?? 0
                    }}
                    onStyleChangeMobile={(margins) => {
                        setLocalStyles(prev => ({
                            ...prev,
                            marginTopMobile: margins.top,
                            marginRightMobile: margins.right,
                            marginBottomMobile: margins.bottom,
                            marginLeftMobile: margins.left
                        }));
                        debouncedParentUpdate({
                            ...element,
                            marginTopMobile: margins.top,
                            marginRightMobile: margins.right,
                            marginBottomMobile: margins.bottom,
                            marginLeftMobile: margins.left
                        });
                    }}
                    
                    // Font Size (⚡ OPTIMISTIC)
                    currentFontSize={localStyles.fontSize ?? (parseInt(element.fontSize) || 16)}
                    onFontSizeChange={(newSize) => {
                        setLocalStyles(prev => ({ ...prev, fontSize: newSize }));
                        debouncedParentUpdate({ ...element, fontSize: newSize });
                    }}
                    currentFontSizeMobile={localStyles.fontSizeMobile ?? (parseInt(element.fontSizeMobile) || 14)}
                    onFontSizeMobileChange={(newSize) => {
                        setLocalStyles(prev => ({ ...prev, fontSizeMobile: newSize }));
                        debouncedParentUpdate({ ...element, fontSizeMobile: newSize });
                    }}
                    
                    // Font Weight (⚡ OPTIMISTIC)
                    currentFontWeight={localStyles.fontWeight ?? (parseInt(element.fontWeight) || 400)}
                    onFontWeightChange={(newWeight) => {
                        setLocalStyles(prev => ({ ...prev, fontWeight: newWeight }));
                        debouncedParentUpdate({ ...element, fontWeight: newWeight });
                    }}
                    currentFontWeightMobile={localStyles.fontWeightMobile ?? (parseInt(element.fontWeightMobile) || 400)}
                    onFontWeightMobileChange={(newWeight) => {
                        setLocalStyles(prev => ({ ...prev, fontWeightMobile: newWeight }));
                        debouncedParentUpdate({ ...element, fontWeightMobile: newWeight });
                    }}
                    
                    // Max Width (⚡ OPTIMISTIC)
                    currentMaxWidth={localStyles.maxWidth ?? (parseInt(element.maxWidth) || 800)}
                    onMaxWidthChange={(newWidth) => {
                        setLocalStyles(prev => ({ ...prev, maxWidth: newWidth }));
                        debouncedParentUpdate({ ...element, maxWidth: newWidth });
                    }}
                    currentMaxWidthMobile={localStyles.maxWidthMobile ?? (parseInt(element.maxWidthMobile) || 600)}
                    onMaxWidthMobileChange={(newWidth) => {
                        setLocalStyles(prev => ({ ...prev, maxWidthMobile: newWidth }));
                        debouncedParentUpdate({ ...element, maxWidthMobile: newWidth });
                    }}
                    
                    // Font Family
                    availableFonts={availableFonts}
                    currentFontFamily={getFontFallback()}
                    onFontFamilyChange={(newFont) => {
                        if (onUpdate) {
                            onUpdate({ ...element, fontFamily: newFont });
                        }
                    }}
                    
                    // Line Height (⚡ OPTIMISTIC)
                    currentLineHeight={localStyles.lineHeight ?? (element.lineHeight ?? 1.6)}
                    onLineHeightChange={(newValue) => {
                        setLocalStyles(prev => ({ ...prev, lineHeight: newValue }));
                        debouncedParentUpdate({ ...element, lineHeight: newValue });
                    }}
                    currentLineHeightMobile={localStyles.lineHeightMobile ?? (element.lineHeightMobile ?? 1.6)}
                    onLineHeightMobileChange={(newValue) => {
                        setLocalStyles(prev => ({ ...prev, lineHeightMobile: newValue }));
                        debouncedParentUpdate({ ...element, lineHeightMobile: newValue });
                    }}
                    
                    // Letter Spacing (⚡ OPTIMISTIC)
                    currentLetterSpacing={localStyles.letterSpacing ?? (element.letterSpacing ?? 0)}
                    onLetterSpacingChange={(newValue) => {
                        setLocalStyles(prev => ({ ...prev, letterSpacing: newValue }));
                        debouncedParentUpdate({ ...element, letterSpacing: newValue });
                    }}
                    currentLetterSpacingMobile={localStyles.letterSpacingMobile ?? (element.letterSpacingMobile ?? 0)}
                    onLetterSpacingMobileChange={(newValue) => {
                        setLocalStyles(prev => ({ ...prev, letterSpacingMobile: newValue }));
                        debouncedParentUpdate({ ...element, letterSpacingMobile: newValue });
                    }}
                    
                    // Text Alignment
                    onTextAlignChange={(newValue) => {
                        if (onUpdate) {
                            onUpdate({ ...element, textAlign: newValue });
                        }
                    }}
                    currentTextAlign={element.textAlign || 'left'}
                    onTextAlignMobileChange={(newValue) => {
                        if (onUpdate) {
                            onUpdate({ ...element, textAlignMobile: newValue });
                        }
                    }}
                    currentTextAlignMobile={element.textAlignMobile || 'left'}
                    
                    // ✅ Text Box (Badge/Tag Style)
                    onTextBoxChange={(updates) => {
                        setLocalStyles(prev => ({ ...prev, ...updates }));
                        debouncedParentUpdate({ ...element, ...updates });
                    }}
                    textBoxEnabled={localStyles.textBoxEnabled ?? (element.textBoxEnabled || false)}
                    textBoxPaddingHorizontal={localStyles.textBoxPaddingHorizontal ?? (element.textBoxPaddingHorizontal || 12)}
                    textBoxPaddingVertical={localStyles.textBoxPaddingVertical ?? (element.textBoxPaddingVertical || 6)}
                    textBoxBackgroundColor={localStyles.textBoxBackgroundColor ?? (element.textBoxBackgroundColor || '#000000')}
                    textBoxBorderEnabled={localStyles.textBoxBorderEnabled ?? (element.textBoxBorderEnabled || false)}
                    textBoxBorderWidth={localStyles.textBoxBorderWidth ?? (element.textBoxBorderWidth || 2)}
                    textBoxBorderColor={localStyles.textBoxBorderColor ?? (element.textBoxBorderColor || '#ffffff')}
                    textBoxBorderRadius={localStyles.textBoxBorderRadius ?? (element.textBoxBorderRadius || 8)}
                    
                    // 🎬 Animation Controls
                    onAnimationChange={(updates) => {
                        if (onUpdate) {
                            onUpdate({ ...element, ...updates });
                        }
                    }}
                    animationEnabled={element.animationEnabled || false}
                    animationType={element.animationType || 'fadeInUp'}
                    animationSpeed={element.animationSpeed || 'normal'}
                    triggerAnimation={element.triggerAnimation}
                    
                    // Mobile Layout
                    isMobileLayout={isMobileLayout}
                    onSelect={onSelect}
                    
                    // Styling
                    // ✅ WICHTIG: rich-text-output NUR im Live View, nicht im Edit Mode
                    // Im Edit Mode würde es TipTap's Paragraph-Struktur zerstören (display: inline auf <p>)
                    className={isEditMode ? "" : "rich-text-output"}
                    style={textStyle}
                />
        </AnimatedElement>
    );
}

export default UniversalTextElement;

